import { t } from '@/i18n';
import { defineServerEnum, reportUnknownServerValue } from '@/shared/lib/utils/server-enum';

import type { AcceptConsentsRequest, ConsentAcceptItem, RequiredConsentItem, TermsAcceptStatusResponse } from '../types';

/**
 * TRAD-3759(W4) — 가입·재동의 «동의 목록»의 순수 규칙. 화면(가입 (가) · 재동의 /terms-agreement (나))과
 * 게이트(PrivateRoute)가 같은 함수를 쓴다 — 목록을 두 곳에서 따로 만들면 «게이트는 막는데 화면엔 항목이 없다»가 된다.
 *
 * 정본: 기획 Confluence 316964930(docs-ops/2026-10-18-consent-flow-plan.md) · 계약 docs-ops/2026-10-18-consent-W1-contract-draft.md
 * · 문구 Confluence 316997725 §3(docs-ops/2026-10-18-TRAD-3762-terms-privacy-consent-copy.md, 990c22c).
 *
 * 🔴 모르는 종류(서버가 먼저 값을 늘린 경우)는 enum 규칙(«알 수 없음»·행동 잠금)의 **예외**다 — 이 화면에서 잠그면 그 사용자는
 * 재동의를 영원히 못 끝낸다. 서버 `displayName`·`documentUrl` 로 그리고 동의를 허용한다(팀장 결정 2026-10-18, 정본표 §5-24 비고,
 * «동의 목록 화면 한정»). 그 밖 화면에서는 이 함수를 쓰지 않는다.
 */
export const CONSENT_TERMS_TYPE = defineServerEnum(['TERMS_OF_SERVICE', 'PRIVACY_POLICY', 'AGE_OVER_14', 'EULA'] as const);

/** G4 — 하우스 룰(커뮤니티 이용 규칙) 원문 주소. `/terms` 는 이용약관 자리다(게시 전까지는 플래그로 하우스 룰을 계속 보여 준다). */
export const HOUSE_RULE_PATH = '/house-rule';

export interface ConsentChecklistItem {
  /** 체크 상태 키 — 서버 종류 문자열 또는 클라 전용 키(광고 수신 등). */
  key: string;
  label: string;
  /** «보기» 링크. 문서가 없는 종류(만 14세)는 null. */
  href: string | null;
  /** S15 — 옛 버전에 동의한 적이 있을 때만(«새 버전으로 바뀌었습니다»). */
  note: string | null;
  /** S8 — 선택 항목 설명(광고 수신). */
  description: string | null;
  required: boolean;
  /** accept-batch 로 보낼 것. 서버 기록 대상이 아닌 항목(광고 수신 — 가입 요청의 별도 필드)은 null. */
  accept: ConsentAcceptItem | null;
}

/** documentUrl 은 서버가 주는 값이라 http(s) 만 링크로 쓴다(javascript: 등 차단). */
const toSafeWebUrl = (raw: string | null): string | null => (raw && /^https?:\/\//i.test(raw) ? raw : null);

export const toConsentChecklistItem = (item: RequiredConsentItem): ConsentChecklistItem => {
  if (!CONSENT_TERMS_TYPE.isKnown(item.termsType)) reportUnknownServerValue('consent.termsType', item.termsType);
  const view = CONSENT_TERMS_TYPE.match<{ label: string; href: string | null }>(item.termsType, {
    TERMS_OF_SERVICE: { label: t('consent.terms'), href: '/terms' },
    PRIVACY_POLICY: { label: t('consent.privacy'), href: '/privacy-policy' },
    AGE_OVER_14: { label: t('consent.age'), href: null },
    EULA: { label: t('consent.house_rules'), href: HOUSE_RULE_PATH },
    UNKNOWN: { label: t('consent.unknown_required', { name: item.displayName }), href: toSafeWebUrl(item.documentUrl) },
  });
  return {
    key: item.termsType,
    label: view.label,
    href: view.href,
    note: item.previouslyAcceptedVersion ? t('reconsent.updated_note') : null,
    description: null,
    required: true,
    accept: { termsType: item.termsType, version: item.version },
  };
};

/**
 * ~~결정 A(팀장 2026-10-18) — web 은 지금까지 로그인 기능 전체에 하우스 룰(EULA) 동의를 요구했고 글쓰기 시점 동의 화면이 없다.
 * 게이트를 required-missing 으로만 바꾸면 하우스 룰 동의 없이 글을 쓰게 되므로, EULA 가 미동의면 같은 목록에 한 줄 더한다.~~
 * 🔴 TRAD-3777(2026-10-19) — 결정 A 철회: 게이트는 더 이상 이 함수를 쓰지 않는다. 지금 호출처는 글·댓글 쓰기 시점 게이트
 * (`features/terms/ui/HouseRuleGate.tsx`)뿐이고, `withHouseRule([], status)` = «물어볼 하우스 룰 줄(0 또는 1)»로 쓴다.
 * 상태를 못 읽었으면(null) 더하지 않는다 — 조회 실패로 막지 않는다(G2).
 */
export const withHouseRule = (
  items: readonly RequiredConsentItem[],
  eula: Pick<TermsAcceptStatusResponse, 'accepted' | 'version'> | null,
): RequiredConsentItem[] =>
  eula && !eula.accepted && !items.some(i => i.termsType === 'EULA')
    ? [...items, { termsType: 'EULA', version: eula.version, previouslyAcceptedVersion: null, documentUrl: null, displayName: '' }]
    : [...items];

export const isRequiredSatisfied = (items: readonly ConsentChecklistItem[], checked: ReadonlySet<string>): boolean =>
  items.filter(i => i.required).every(i => checked.has(i.key));

export const isAllChecked = (items: readonly ConsentChecklistItem[], checked: ReadonlySet<string>): boolean =>
  items.length > 0 && items.every(i => checked.has(i.key));

/** «전체 동의» — 켜면 전부, 끄면 전부 해제. 따로 기록하지 않는다(S3). */
export const toggleAll = (items: readonly ConsentChecklistItem[], on: boolean): Set<string> =>
  new Set(on ? items.map(i => i.key) : []);

export const toggleOne = (checked: ReadonlySet<string>, key: string): Set<string> => {
  const next = new Set(checked);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
};

export const toAcceptConsentsRequest = (
  items: readonly ConsentChecklistItem[],
  checked: ReadonlySet<string>,
): AcceptConsentsRequest => ({
  items: items.flatMap(i => (i.accept && checked.has(i.key) ? [i.accept] : [])),
});

/**
 * S12/S13 — 재동의 제목. 목록이 «새 버전으로 바뀐 문서 하나»(이용약관 또는 처리방침)뿐일 때만 S13(«{document}이 바뀌었습니다»),
 * 그 밖(처음 묻는 항목이 섞임 · 바뀐 문서가 둘 · 문서 이름을 모르는 종류)은 범용 S12(«필수 항목에 동의해 주세요»).
 * 🔴 S12 원문(«서비스 이용약관에 동의해 주세요»)은 만 14세 확인만 빠진 실응답에서 틀렸다(web 시안 지적 → planner b4465e4).
 */
export const reconsentTitle = (items: readonly RequiredConsentItem[]): string => {
  const only = items.length === 1 ? items[0] : null;
  if (!only?.previouslyAcceptedVersion) return t('reconsent.title.new');
  return CONSENT_TERMS_TYPE.match(only.termsType, {
    TERMS_OF_SERVICE: t('reconsent.title.updated', { document: t('consent.document.terms') }),
    PRIVACY_POLICY: t('reconsent.title.updated', { document: t('consent.document.privacy') }),
    AGE_OVER_14: t('reconsent.title.new'),
    EULA: t('reconsent.title.new'),
    UNKNOWN: t('reconsent.title.new'),
  });
};

/** G4 — `/terms` 가 보여 줄 문서 종류. 게시 전(false)은 종전대로 하우스 룰. */
export const termsPageTermsType = (published: boolean): 'TERMS_OF_SERVICE' | 'EULA' =>
  published ? 'TERMS_OF_SERVICE' : 'EULA';

/**
 * 3사 공통 규칙(팀장 2026-10-18, android 제안 채택) — 재동의 게이트 화면의 «모양».
 * 서버에 «새 회원» 신호가 없어서 미동의 목록으로 가른다:
 *  - 전부 `previouslyAcceptedVersion == null`(처음 묻는 것뿐 — 소셜 첫 로그인·기록 없는 기존 회원) → 'first' = 가입 (가) 모양(S1·S2)
 *  - 하나라도 옛 버전 동의가 있음(새 버전) → 'updated' = 재동의 (나) 모양(S12/S13 · S14)
 * 두 모양 모두 하단 로그아웃·탈퇴(D4). 가입 화면은 이 함수와 무관하게 항상 (가).
 */
export type ConsentScreenShape = 'first' | 'updated';

export const consentScreenShape = (items: readonly RequiredConsentItem[]): ConsentScreenShape =>
  items.some(i => i.previouslyAcceptedVersion) ? 'updated' : 'first';

/**
 * 가입 2단계(davidyoon 2026-10-18 «약관 동의를 한 단계 더», 3사 공통 — 기획 316964930 v6 §2 (가-2)).
 * ① 약관 동의 → «다음» → ② 정보 입력(`?step=info`). 브라우저 뒤로 = ①.
 *  - 목록 조회 실패 → ②로 바로(동의 기록 없이 가입 → 첫 로그인 때 게이트가 묻는다 · fail-open, 팀장 결정 ③)
 *  - 불러오는 중 → ①(로딩 표시)
 *  - ②를 요청했지만 필수 미충족(주소를 직접 열었거나 새로고침) → ①로 되돌린다(동의 없이 ②에 닿지 않게)
 */
export type SignupStep = 'consent' | 'info';
export type ConsentListState = 'loading' | 'ok' | 'error';

export const resolveSignupStep = (requested: string | null, list: ConsentListState, satisfied: boolean): SignupStep => {
  if (list === 'error') return 'info';
  if (list === 'loading') return 'consent';
  return requested === 'info' && satisfied ? 'info' : 'consent';
};

/** 동의 게이트 화면 주소. 원래 있던 곳(`from`)을 붙여 동의 뒤 돌아가게 한다(G7 · PrivateRoute 공용). */
export const CONSENT_GATE_PATH = '/terms-agreement';

export const consentGateHref = (from: string | null | undefined): string => {
  const back = safeReturnPath(from);
  return back ? `${CONSENT_GATE_PATH}?from=${encodeURIComponent(back)}` : CONSENT_GATE_PATH;
};

/**
 * 🔴 동의 뒤 돌아갈 주소 — «이 사이트 안의 경로»만 받는다(open redirect 방지).
 * `/` 로 시작하고 `//`·`/\` 로 시작하지 않으며(프로토콜 상대 URL), 동의 화면 자신이 아니어야 한다. 아니면 null(→ 홈).
 */
export const safeReturnPath = (raw: string | null | undefined): string | null => {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
  // 제어 문자(줄바꿈 등)가 섞이면 버린다 — 정규식 대신 코드 값으로 본다(no-control-regex).
  if ([...raw].some(ch => ch.charCodeAt(0) < 0x20)) return null;
  const pathOnly = raw.split(/[?#]/)[0];
  if (pathOnly === CONSENT_GATE_PATH) return null;
  return raw;
};
